import React, { useEffect, useMemo, useState, useCallback } from "react";

/**
 * ============================================================
 * AdminHomeBanner.jsx
 * ============================================================
 *
 * RÔLE
 * ------------------------------------------------------------
 * Page admin dédiée à la gestion de la bannière promo home.
 *
 * IMPORTANT
 * ------------------------------------------------------------
 * Les endpoints backend de cette page sont protégés par auth réelle.
 * Il faut donc envoyer le bearer token à chaque requête.
 *
 * Ce composant :
 * - charge la liste des produits configurables bannière
 * - permet de filtrer localement
 * - permet de modifier show_in_home_banner / banner_rank
 * - sauvegarde chaque produit individuellement
 */

const API_LIST =
  "https://marchedeluxe.ch/services/get_produits_banniere_admin.php";
const API_SAVE =
  "https://marchedeluxe.ch/services/sauvegarder_produit_banniere.php";

/**
 * ============================================================
 * GUIDE — MOTS-CLÉS CAVE
 * ============================================================
 *
 * On garde une détection tolérante similaire au dashboard admin.
 */
const CAVE_CATEGORY_KEYWORDS = [
  "cave",
  "univers cave",
  "vin",
  "vins",
  "bière",
  "bières",
  "biere",
  "bieres",
  "alcool",
  "alcools",
  "alcool fort",
  "alcools forts",
  "spiritueux",
  "champagne",
  "whisky",
  "rhum",
  "gin",
  "vodka",
  "liqueur",
];

/**
 * ============================================================
 * GUIDE — HELPERS DE NORMALISATION
 * ============================================================
 *
 * Ces helpers gardent la lecture des données souple et compatible
 * avec l’existant legacy.
 */
function normalizeCategory(value) {
  return String(value || "")
    .trim()
    .toLowerCase();
}

function isCaveCategory(value) {
  return CAVE_CATEGORY_KEYWORDS.includes(normalizeCategory(value));
}

function getProductCategory(product) {
  return String(product?.category || product?.categorie || "").trim();
}

function isCaveProduct(product) {
  if (!product) return false;

  if (product?.__isCave === true) return true;
  if (product?.__productScope === "cave") return true;
  if (product?.product_scope === "cave") return true;
  if (product?.source === "cave") return true;
  if (Number(product?.isCave || 0) === 1) return true;

  return isCaveCategory(getProductCategory(product));
}

function normalizeMilkType(value) {
  return typeof value === "string" ? value.trim() : "";
}

/**
 * ============================================================
 * GUIDE — LECTURE DU TOKEN FRONT
 * ============================================================
 *
 * Le projet stocke déjà le token auth côté front.
 * On le relit depuis le localStorage pour l’envoyer vers les
 * endpoints backend protégés.
 *
 * IMPORTANT :
 * - on ne fait ici qu’une lecture UI du token
 * - la vraie sécurité reste côté backend
 */
function getStoredUserToken() {
  try {
    return localStorage.getItem("user_token") || "";
  } catch (error) {
    return "";
  }
}

/**
 * ============================================================
 * GUIDE — HEADERS AUTHENTIFIÉS
 * ============================================================
 *
 * Ce helper centralise la construction des headers HTTP :
 * - Authorization si token présent
 * - Content-Type JSON si nécessaire
 *
 * Cela évite d’oublier l’auth sur un endpoint admin.
 */
function buildAuthHeaders(extraHeaders = {}) {
  const token = getStoredUserToken();

  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extraHeaders,
  };
}

/**
 * ============================================================
 * GUIDE — LECTURE DE RÉPONSE JSON ROBUSTE
 * ============================================================
 *
 * Certains endpoints peuvent répondre avec :
 * - success: false
 * - 401 / 403
 * - ou un body non conforme si erreur serveur
 *
 * On garde une lecture défensive pour éviter les crashs frontend.
 */
async function readJsonSafely(response) {
  try {
    return await response.json();
  } catch (error) {
    return null;
  }
}

export default function AdminHomeBanner() {
  /**
   * ============================================================
   * GUIDE — ÉTATS PRINCIPAUX
   * ============================================================
   *
   * items      : liste brute renvoyée par le backend
   * loading    : état de chargement initial / refresh
   * savingId   : id du produit en cours de sauvegarde
   * message    : message UI simple pour succès / erreur
   */
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);
  const [message, setMessage] = useState("");

  /**
   * ============================================================
   * GUIDE — FILTRES UI
   * ============================================================
   *
   * Filtres purement frontend appliqués sur la liste déjà chargée.
   */
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [milkTypeFilter, setMilkTypeFilter] = useState("");
  const [scopeFilter, setScopeFilter] = useState("all"); // all | classic | cave
  const [onlineFilter, setOnlineFilter] = useState("all"); // all | yes | no
  const [stockFilter, setStockFilter] = useState("all"); // all | in | out

  /**
   * ============================================================
   * GUIDE — CHARGEMENT INITIAL
   * ============================================================
   *
   * Au montage du composant, on charge la liste admin bannière.
   */
  useEffect(() => {
    fetchItems();
  }, []);

  /**
   * ============================================================
   * GUIDE — CHARGEMENT AUTHENTIFIÉ DES PRODUITS
   * ============================================================
   *
   * Cause du bug initial :
   * l’ancien code appelait l’endpoint protégé sans Authorization.
   *
   * Ici on envoie maintenant :
   * Authorization: Bearer <token>
   *
   * Effet attendu :
   * - si token valide + rôle staff/admin => la liste remonte
   * - si token absent/expiré => message clair
   */
  const fetchItems = useCallback(async () => {
    setLoading(true);
    setMessage("");

    try {
      const token = getStoredUserToken();
      if (!token) {
        throw new Error("Session admin absente. Merci de vous reconnecter.");
      }

      const res = await fetch(API_LIST, {
        method: "GET",
        headers: buildAuthHeaders(),
      });

      const json = await readJsonSafely(res);

      if (res.status === 401) {
        throw new Error("Session expirée ou non authentifiée.");
      }

      if (res.status === 403) {
        throw new Error("Accès refusé à cette page admin.");
      }

      if (!res.ok) {
        throw new Error(
          json?.error || "Erreur lors du chargement des produits bannière."
        );
      }

      if (!json?.success) {
        throw new Error(json?.error || "Erreur chargement");
      }

      setItems(Array.isArray(json.items) ? json.items : []);
    } catch (err) {
      setItems([]);
      setMessage(err?.message || "Erreur chargement");
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * ============================================================
   * GUIDE — LISTE DES CATÉGORIES
   * ============================================================
   *
   * On dérive dynamiquement les catégories à partir des items reçus.
   */
  const categories = useMemo(() => {
    const set = new Set();

    items.forEach((item) => {
      const cat = getProductCategory(item);
      if (cat) set.add(cat);
    });

    return Array.from(set).sort((a, b) => a.localeCompare(b, "fr"));
  }, [items]);

  /**
   * ============================================================
   * GUIDE — LISTE DES TYPES DE LAIT
   * ============================================================
   *
   * On lit les valeurs réellement présentes dans les données.
   * Aucun hardcode métier inutile ici.
   */
  const milkTypes = useMemo(() => {
    const set = new Set();

    items.forEach((item) => {
      const milk = normalizeMilkType(item?.type_lait);
      if (milk) set.add(milk);
    });

    return Array.from(set).sort((a, b) => a.localeCompare(b, "fr"));
  }, [items]);

  /**
   * ============================================================
   * GUIDE — FILTRAGE LOCAL
   * ============================================================
   *
   * Cette logique ne touche pas le backend.
   * Elle travaille uniquement sur items déjà chargés.
   */
  const filteredItems = useMemo(() => {
    const searchValue = search.trim().toLowerCase();

    return items.filter((item) => {
      const name = String(item?.name || "").toLowerCase();
      const id = String(item?.id || "");
      const category = getProductCategory(item);
      const milk = normalizeMilkType(item?.type_lait);
      const cave = isCaveProduct(item);
      const isOnline = Number(item?.isOnline || 0) === 1;
      const stock = Number(item?.stock || 0);
      const inStock = stock > 0;

      const matchSearch =
        !searchValue || name.includes(searchValue) || id.includes(searchValue);

      const matchCategory = !categoryFilter || category === categoryFilter;
      const matchMilkType = !milkTypeFilter || milk === milkTypeFilter;

      const matchScope =
        scopeFilter === "all" ||
        (scopeFilter === "cave" && cave) ||
        (scopeFilter === "classic" && !cave);

      const matchOnline =
        onlineFilter === "all" ||
        (onlineFilter === "yes" && isOnline) ||
        (onlineFilter === "no" && !isOnline);

      const matchStock =
        stockFilter === "all" ||
        (stockFilter === "in" && inStock) ||
        (stockFilter === "out" && !inStock);

      return (
        matchSearch &&
        matchCategory &&
        matchMilkType &&
        matchScope &&
        matchOnline &&
        matchStock
      );
    });
  }, [
    items,
    search,
    categoryFilter,
    milkTypeFilter,
    scopeFilter,
    onlineFilter,
    stockFilter,
  ]);

  /**
   * ============================================================
   * GUIDE — ÉDITION LOCALE DU FLAG BANNIÈRE
   * ============================================================
   *
   * On modifie l’état local immédiatement pour une UX fluide.
   * La sauvegarde réelle se fait ensuite via saveItem().
   */
  const handleToggle = (id, value) => {
    setItems((prev) =>
      prev.map((it) =>
        it.id === id ? { ...it, show_in_home_banner: value ? 1 : 0 } : it
      )
    );
  };

  /**
   * ============================================================
   * GUIDE — ÉDITION LOCALE DE L’ORDRE
   * ============================================================
   *
   * On normalise en nombre entier.
   * Valeur fallback : 9999 si saisie invalide.
   */
  const handleRankChange = (id, value) => {
    const v = Number(value);

    setItems((prev) =>
      prev.map((it) =>
        it.id === id
          ? { ...it, banner_rank: Number.isFinite(v) ? v : 9999 }
          : it
      )
    );
  };

  /**
   * ============================================================
   * GUIDE — SAUVEGARDE AUTHENTIFIÉE
   * ============================================================
   *
   * Comme pour le GET, le POST doit envoyer le bearer token.
   *
   * Sans cela :
   * - 401 Unauthorized
   * - aucune sauvegarde réelle
   */
  const saveItem = async (item) => {
    setSavingId(item.id);
    setMessage("");

    try {
      const token = getStoredUserToken();
      if (!token) {
        throw new Error("Session admin absente. Merci de vous reconnecter.");
      }

      const res = await fetch(API_SAVE, {
        method: "POST",
        headers: buildAuthHeaders({
          "Content-Type": "application/json",
        }),
        body: JSON.stringify({
          produit_id: item.id,
          show_in_home_banner: item.show_in_home_banner || 0,
          banner_rank: item.banner_rank || 9999,
        }),
      });

      const json = await readJsonSafely(res);

      if (res.status === 401) {
        throw new Error("Session expirée ou non authentifiée.");
      }

      if (res.status === 403) {
        throw new Error(
          "Vous n’avez pas les droits pour modifier la bannière."
        );
      }

      if (!res.ok) {
        throw new Error(
          json?.error || "Erreur lors de la sauvegarde du produit."
        );
      }

      if (!json?.success) {
        throw new Error(json?.error || "Erreur sauvegarde");
      }

      setMessage(`Produit #${item.id} sauvegardé.`);
    } catch (err) {
      setMessage(err?.message || "Erreur sauvegarde");
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <h1>Gestion bannière promo</h1>

      {/* =======================================================
          GUIDE — BARRE DE FILTRES
          -------------------------------------------------------
          Filtres purement frontend pour retrouver rapidement
          le produit à configurer dans la liste admin.
         ======================================================= */}
      <div
        style={{
          display: "flex",
          gap: 12,
          flexWrap: "wrap",
          marginBottom: 16,
          alignItems: "center",
        }}
      >
        <input
          type="text"
          placeholder="Recherche nom ou ID"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
        >
          <option value="">Toutes catégories</option>
          {categories.map((cat) => (
            <option key={cat} value={cat}>
              {cat}
            </option>
          ))}
        </select>

        <select
          value={milkTypeFilter}
          onChange={(e) => setMilkTypeFilter(e.target.value)}
        >
          <option value="">Tous types de lait</option>
          {milkTypes.map((milk) => (
            <option key={milk} value={milk}>
              {milk}
            </option>
          ))}
        </select>

        <select
          value={scopeFilter}
          onChange={(e) => setScopeFilter(e.target.value)}
        >
          <option value="all">Tous types</option>
          <option value="classic">Classiques</option>
          <option value="cave">Cave</option>
        </select>

        <select
          value={onlineFilter}
          onChange={(e) => setOnlineFilter(e.target.value)}
        >
          <option value="all">Online : tous</option>
          <option value="yes">Online : oui</option>
          <option value="no">Online : non</option>
        </select>

        <select
          value={stockFilter}
          onChange={(e) => setStockFilter(e.target.value)}
        >
          <option value="all">Stock : tous</option>
          <option value="in">En stock</option>
          <option value="out">Rupture</option>
        </select>

        <button onClick={fetchItems}>Rafraîchir</button>
      </div>

      {/* =======================================================
          GUIDE — MESSAGE D’ÉTAT
          -------------------------------------------------------
          Zone simple pour afficher :
          - erreur auth
          - erreur chargement
          - confirmation de sauvegarde
         ======================================================= */}
      {message && (
        <div style={{ marginBottom: 12, padding: 10, background: "#f5f5f5" }}>
          {message}
        </div>
      )}

      {loading ? (
        <p>Chargement...</p>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th>ID</th>
                <th>Nom</th>
                <th>Catégorie</th>
                <th>Lait</th>
                <th>Type</th>
                <th>Promo</th>
                <th>Online</th>
                <th>Stock</th>
                <th>Bannière</th>
                <th>Ordre</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {filteredItems.map((item) => {
                const cave = isCaveProduct(item);
                const milk = normalizeMilkType(item?.type_lait);

                return (
                  <tr key={item.id} style={{ borderTop: "1px solid #ddd" }}>
                    <td>{item.id}</td>
                    <td>{item.name}</td>
                    <td>{getProductCategory(item) || "—"}</td>
                    <td>{milk || "—"}</td>
                    <td>{cave ? "Cave" : "Classique"}</td>
                    <td>{Number(item.isDiscount) === 1 ? "Oui" : "Non"}</td>
                    <td>{Number(item.isOnline) === 1 ? "Oui" : "Non"}</td>
                    <td>{item.stock}</td>

                    <td>
                      <input
                        type="checkbox"
                        checked={Number(item.show_in_home_banner) === 1}
                        onChange={(e) =>
                          handleToggle(item.id, e.target.checked)
                        }
                      />
                    </td>

                    <td>
                      <input
                        type="number"
                        value={item.banner_rank || 9999}
                        onChange={(e) =>
                          handleRankChange(item.id, e.target.value)
                        }
                        style={{ width: 70 }}
                      />
                    </td>

                    <td>
                      <button
                        onClick={() => saveItem(item)}
                        disabled={savingId === item.id}
                      >
                        {savingId === item.id ? "..." : "Sauver"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {!filteredItems.length && <p>Aucun produit trouvé.</p>}
        </div>
      )}
    </div>
  );
}
